import { apiClient } from './apiClient';

export const examinerRequest = async (sessionId, path, { method = 'GET', body, signal, binary = false, idempotencyKey } = {}) => {
  const multipart = body instanceof FormData;
  const response = await apiClient(`/examiner${path}`, {
    method, signal, cache: 'no-store',
    headers: { 'session-id': sessionId, ...(body && !multipart ? { 'Content-Type': 'application/json' } : {}), ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) },
    ...(body ? { body: multipart ? body : JSON.stringify(body) } : {})
  });
  if (!response.ok) {
    let detail;
    try { detail = await response.json(); } catch { detail = null; }
    const failure = new Error(detail?.error || `Examiner request failed (${response.status}).`);
    failure.status = response.status;
    const retry = response.headers?.get('Retry-After');
    failure.retryAt = response.status === 429
      ? Date.now() + (retry && Number.isFinite(Number(retry)) ? Number(retry) * 1000 : Math.max(60000, Date.parse(retry) - Date.now() || 0)) : null;
    failure.path = path;
    failure.readiness = detail?.readiness;
    throw failure;
  }
  return binary ? response.blob() : response.json();
};

export const examinerLimitFields = [
  ['sessionMinutes', 'Maximum examination duration (minutes)', 1, 180],
  ['userSessionsPerDay', 'Sessions per user per UTC day', 1, 100],
  ['userMinutesPerDay', 'Reserved minutes per user per UTC day', 1, 1440],
  ['organizationMinutesPerDay', 'Reserved minutes for the organization per UTC day', 1, 100000],
  ['responsesPerSession', 'AI responses per examination', 1, 5000],
  ['toolCallsPerSession', 'Reference/assessment calls per examination', 1, 5000],
  ['httpRequestsPerMinute', 'Examiner API requests per user per minute', 10, 600],
  ['importsPerMinute', 'Official reference imports per organization per minute', 1, 60],
  ['audioDownloadsPerMinute', 'Recording downloads per user per minute', 1, 60]
];

export const validateExaminerLimits = values => {
  for (const [key, label, min, max] of examinerLimitFields) {
    if (values[key] === '' || !Number.isInteger(Number(values[key])) || Number(values[key]) < min || Number(values[key]) > max) {
      return `${label} must be an integer from ${min} to ${max}.`;
    }
  }
  if (Number(values.userMinutesPerDay) < Number(values.sessionMinutes)) return 'User daily minutes must cover one examination.';
  if (Number(values.organizationMinutesPerDay) < Number(values.userMinutesPerDay)) return 'Organization daily minutes must cover the user daily minutes.';
  return '';
};

export const mergeExaminerTranscript = (previous, incoming) => {
  const next = new Map(previous.map(item => [item.event_key || item.eventKey, item]));
  for (const item of incoming) {
    const key = item.event_key || item.eventKey;
    if (key) next.set(key, { ...next.get(key), ...item });
  }
  return [...next.values()];
};

export const examinerScope = currentUser => `${currentUser?.sessionId}:${currentUser?.user?.id ?? currentUser?.id}:${currentUser?.user?.orgId}`;

export const examinerLaunchSettings = curriculum => {
  const settings = curriculum?.launchSettings;
  if (!Number.isSafeInteger(curriculum?.launchRevision) || curriculum.launchRevision < 1
    || !settings || !Number.isSafeInteger(settings.aircraftId) || settings.aircraftId < 1
    || !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(settings.examinerProfileId || '')
    || !['practice', 'graded'].includes(settings.mode)
    || typeof settings.retainTranscript !== 'boolean' || typeof settings.retainAudio !== 'boolean'
    || (settings.mode === 'graded' && (!settings.retainTranscript || !curriculum.gradedAvailable))) return null;
  return {
    aircraftId: settings.aircraftId, examinerProfileId: settings.examinerProfileId, mode: settings.mode,
    retainTranscript: settings.retainTranscript, retainAudio: settings.retainAudio
  };
};
